import type { HapticType } from './types'

type HapticEventParams =
  | { type: 'impact'; impact_style: 'light' | 'medium' | 'heavy' | 'rigid' | 'soft' }
  | { type: 'notification'; notification_type: 'error' | 'success' | 'warning' }
  | { type: 'selection_change' }

const HAPTIC_EVENT_PARAMS: Record<HapticType, HapticEventParams> = {
  selection: { type: 'selection_change' },
  impactLight: { type: 'impact', impact_style: 'light' },
  impactMedium: { type: 'impact', impact_style: 'medium' },
  impactHeavy: { type: 'impact', impact_style: 'heavy' },
  soft: { type: 'impact', impact_style: 'soft' },
  rigid: { type: 'impact', impact_style: 'rigid' },
  notificationSuccess: { type: 'notification', notification_type: 'success' },
  notificationWarning: { type: 'notification', notification_type: 'warning' },
  notificationError: { type: 'notification', notification_type: 'error' },
}

const MOBILE_TELEGRAM_PLATFORMS = new Set(['ios', 'android', 'android_x'])

function getTelegram() {
  if (typeof window === 'undefined') {
    return undefined
  }

  return window as unknown as {
    TelegramWebviewProxy?: { postEvent?: (eventType: string, data: string) => void }
    Telegram?: { WebApp?: { platform?: string } }
  }
}

function getPlatform(): string | undefined {
  return getTelegram()?.Telegram?.WebApp?.platform
}

export function isTelegramHapticAvailable(): boolean {
  const proxy = getTelegram()?.TelegramWebviewProxy

  if (typeof proxy?.postEvent !== 'function') {
    return false
  }

  const platform = getPlatform()

  if (platform && platform !== 'unknown') {
    return MOBILE_TELEGRAM_PLATFORMS.has(platform)
  }

  return typeof navigator !== 'undefined' && /iPhone|iPad|iPod|Android/i.test(navigator.userAgent)
}

// Telegram Android баг: impact/selection не вибрируют, работает только notification
// (github.com/Telegram-Mini-Apps/issues/issues/28). Маппим на notification.
function toAndroidParams(params: HapticEventParams): HapticEventParams {
  if (params.type === 'notification') {
    return params
  }

  if (params.type === 'impact') {
    const notificationType = params.impact_style === 'heavy' ? 'error' : 'success'
    return { type: 'notification', notification_type: notificationType }
  }

  return { type: 'notification', notification_type: 'success' }
}

export function triggerTelegramHaptic(type: HapticType): boolean {
  // Шлём хаптик низкоуровневым bridge Telegram (как @tma.js/sdk-react) — легаси
  // WebApp.HapticFeedback.impactOccurred на Android не срабатывает.
  const platform = getPlatform()
  const proxy = getTelegram()?.TelegramWebviewProxy

  if (!isTelegramHapticAvailable() || typeof proxy?.postEvent !== 'function') {
    return false
  }

  const isAndroid = platform?.startsWith('android')
  const params = isAndroid ? toAndroidParams(HAPTIC_EVENT_PARAMS[type]) : HAPTIC_EVENT_PARAMS[type]

  try {
    proxy.postEvent('web_app_trigger_haptic_feedback', JSON.stringify(params))
    return true
  } catch {
    // Android WebView: Java-мост может бросить (object is gone / method not found).
    return false
  }
}
